import { useState } from "react";
import { adminFetch } from "../../lib/adminApi";
import { toast } from "sonner";
import { type TimelineEvent } from "./MilestoneEditForm";
import { professionalTimeline, academicTimeline, communityTimeline } from "../../lib/config";

export function safeParseDetails(details: any): string[] {
  if (!details) return [];
  if (Array.isArray(details)) return details;
  try {
    const parsed = typeof details === "string" ? JSON.parse(details) : details;
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [details];
  }
}

export function safeParseLinks(links: any): any[] {
  if (!links) return [];
  if (Array.isArray(links)) return links;
  try {
    const parsed = typeof links === "string" ? JSON.parse(links) : links;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function useTimelineDraft(
  type: string,
  editingEvent: Partial<TimelineEvent> | null,
  setEditingEvent: React.Dispatch<React.SetStateAction<Partial<TimelineEvent> | null>>,
  filteredEvents: TimelineEvent[],
  detailsEn: string[],
  detailsTr: string[],
  setDetailsEn: React.Dispatch<React.SetStateAction<string[]>>,
  setDetailsTr: React.Dispatch<React.SetStateAction<string[]>>,
  activeDraft: any,
  setActiveDraft: React.Dispatch<React.SetStateAction<any>>
) {
  const handleSaveDraft = async () => {
    if (!editingEvent) return;
    try {
      const draftPayload = {
        ...editingEvent,
        details_en: JSON.stringify(detailsEn.filter((d) => d.trim().length > 0)),
        details_tr: JSON.stringify(detailsTr.filter((d) => d.trim().length > 0)),
        links: typeof editingEvent.links === "string" ? editingEvent.links : JSON.stringify(editingEvent.links || [])
      };
      await adminFetch("/api/admin/drafts", {
        operation: "save",
        item_type: `timeline_${type}`,
        item_id: editingEvent.id,
        draft_data: draftPayload,
      });
      setActiveDraft({
        ...draftPayload,
        links: safeParseLinks(draftPayload.links)
      });
      toast.success("Timeline draft saved to staging successfully!");
    } catch (err: any) {
      toast.error(err.message || "Failed to save draft");
    }
  };

  const handleDiscardDraft = async () => {
    if (!editingEvent) return;
    if (!confirm("Are you sure you want to discard this staged draft?")) return;
    try {
      await adminFetch("/api/admin/drafts", {
        operation: "delete",
        item_type: `timeline_${type}`,
        item_id: editingEvent.id,
      });
      setActiveDraft(null);
      const live = filteredEvents.find((e) => e.id === editingEvent.id);
      if (live) {
        setEditingEvent(live);
        setDetailsEn(safeParseDetails(live.details_en));
        setDetailsTr(safeParseDetails(live.details_tr));
      } else {
        setEditingEvent(null);
      }
      toast.success("Staged draft discarded successfully!");
    } catch (err: any) {
      toast.error(err.message || "Failed to discard draft");
    }
  };

  return { handleSaveDraft, handleDiscardDraft };
}

export function useTimelineCrud(
  type: string,
  editingEvent: Partial<TimelineEvent> | null,
  setEditingEvent: React.Dispatch<React.SetStateAction<Partial<TimelineEvent> | null>>,
  activeDraft: any,
  setActiveDraft: React.Dispatch<React.SetStateAction<any>>,
  detailsEn: string[],
  detailsTr: string[],
  onRefresh: () => void,
  filteredEvents: TimelineEvent[]
) {
  const handleSave = async () => {
    if (!editingEvent?.company_en || !editingEvent?.role_en) {
      toast.error("Company Name and Role (English) are required!");
      return;
    }
    try {
      const payload = {
        ...editingEvent,
        details_en: detailsEn.filter((d) => d.trim().length > 0),
        details_tr: detailsTr.filter((d) => d.trim().length > 0),
        links: typeof editingEvent.links === "string" ? JSON.parse(editingEvent.links) : (editingEvent.links || [])
      };
      await adminFetch("/api/admin/drafts", {
        operation: "promote",
        item_type: `timeline_${type}`,
        item_id: editingEvent.id,
        draft_data: payload,
      });
      toast.success("Timeline milestone saved successfully!");
      setEditingEvent(null);
      setActiveDraft(null);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to save milestone");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this milestone?")) return;
    try {
      await adminFetch("/api/admin/timeline", { operation: "delete", id });
      await adminFetch("/api/admin/drafts", {
        operation: "delete",
        item_type: `timeline_${type}`,
        item_id: id,
      });
      toast.success("Milestone deleted successfully!");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete milestone");
    }
  };

  const handleMove = async (index: number, direction: "up" | "down") => {
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= filteredEvents.length) return;

    const eventA = filteredEvents[index];
    const eventB = filteredEvents[swapIndex];
    if (!eventA || !eventB) return;

    try {
      await adminFetch("/api/admin/timeline", { ...eventA, sort_order: eventB.sort_order });
      await adminFetch("/api/admin/timeline", { ...eventB, sort_order: eventA.sort_order });
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to reorder milestones");
    }
  };

  return { handleSave, handleDelete, handleMove };
}

export function useTimelineManager(
  type: "professional" | "academic" | "community",
  filteredEvents: TimelineEvent[],
  onRefresh: () => void
) {
  const [editingEvent, setEditingEvent] = useState<Partial<TimelineEvent> | null>(null);
  const [detailsEn, setDetailsEn] = useState<string[]>([]);
  const [detailsTr, setDetailsTr] = useState<string[]>([]);
  const [activeDraft, setActiveDraft] = useState<any>(null);

  const { handleSaveDraft, handleDiscardDraft } = useTimelineDraft(
    type,
    editingEvent,
    setEditingEvent,
    filteredEvents,
    detailsEn,
    detailsTr,
    setDetailsEn,
    setDetailsTr,
    activeDraft,
    setActiveDraft
  );

  const { handleSave, handleDelete, handleMove } = useTimelineCrud(
    type,
    editingEvent,
    setEditingEvent,
    activeDraft,
    setActiveDraft,
    detailsEn,
    detailsTr,
    onRefresh,
    filteredEvents
  );

  const startAdd = () => {
    setActiveDraft(null);
    setEditingEvent({
      id: `${type}-${Date.now()}`,
      type,
      year: new Date().getFullYear().toString(),
      company_en: "",
      company_tr: "",
      role_en: "",
      role_tr: "",
      duration: "",
      logo_url: "",
      sort_order: filteredEvents.length,
      links: []
    });
    setDetailsEn([""]);
    setDetailsTr([""]);
  };

  const startEdit = (event: TimelineEvent) => {
    const parsedEvent = {
      ...event,
      links: safeParseLinks(event.links)
    };
    setEditingEvent(parsedEvent);
    setDetailsEn(safeParseDetails(event.details_en));
    setDetailsTr(safeParseDetails(event.details_tr));

    adminFetch(`/api/admin/drafts?item_type=timeline_${type}&item_id=${event.id}`)
      .then((res) => {
        if (res?.success && res.draft) {
          const draftParsed = {
            ...res.draft,
            links: safeParseLinks(res.draft.links)
          };
          setActiveDraft(draftParsed);
          setEditingEvent(draftParsed);
          setDetailsEn(safeParseDetails(res.draft.details_en));
          setDetailsTr(safeParseDetails(res.draft.details_tr));
          toast.info("Unpublished staged draft loaded for editing.");
        } else {
          setActiveDraft(null);
        }
      })
      .catch(() => setActiveDraft(null));
  };

  return {
    editingEvent,
    setEditingEvent,
    detailsEn,
    setDetailsEn,
    detailsTr,
    setDetailsTr,
    activeDraft,
    setActiveDraft,
    handleSaveDraft,
    handleDiscardDraft,
    startAdd,
    startEdit,
    handleSave,
    handleDelete,
    handleMove,
  };
}
